import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import React from 'react';
import { Link } from 'react-router-dom';

import { MatterSelectorCombobox } from '@/components/matter/MatterSelectorCombobox';
import {
  useGetMatterCompanionsQuery,
  useGetMatterDetailsQuery,
  usePatchMatterCompanionMembersMutation,
  usePatchMatterCompanionsMutation,
} from '@/services/api/mattersService';

const getErrorMessage = (
  error: FetchBaseQueryError | SerializedError,
): string => {
  if ('data' in error && error.data && typeof error.data === 'object') {
    const data = error.data as {
      detail?: string;
      message?: string;
      error?: string;
    };
    return data.detail || data.message || data.error || 'An error occurred';
  }
  if ('message' in error && error.message) {
    return error.message;
  }
  return 'An error occurred';
};

interface ManageCompanionsModalProps {
  matterId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ManageCompanionsModal: React.FC<ManageCompanionsModalProps> = ({
  matterId,
  isOpen,
  onClose,
}) => {
  const { data: companions } = useGetMatterCompanionsQuery(matterId, {
    skip: !matterId,
  });
  const { data: matter } = useGetMatterDetailsQuery(matterId, {
    skip: !matterId,
  });
  const firmId = matter?.firm?.id;
  const [selectedMatterId, setSelectedMatterId] = React.useState('');
  const [addError, setAddError] = React.useState('');
  const [makePrimary, { isLoading: isMakingPrimary }] =
    usePatchMatterCompanionsMutation();
  const [patchMembers, { isLoading: isPatchingMembers }] =
    usePatchMatterCompanionMembersMutation();

  const isCurrentPrimary = !!companions?.members.find(
    (m) => m.is_primary && m.id === matterId,
  );
  const primaryMember = companions?.members.find((m) => m.is_primary) ?? null;
  const nonPrimaryMembers =
    companions?.members.filter((m) => !m.is_primary) ?? [];

  const handleMakePrimary = async (targetMatterId: string) => {
    await makePrimary({
      matter_id: targetMatterId,
      make_primary: true,
    }).unwrap();
  };

  const handleAddSelectedMember = async () => {
    if (!matterId) return;
    if (!selectedMatterId) return;
    const alreadyMember = companions?.members.some(
      (m) => m.id === selectedMatterId,
    );
    if (alreadyMember) return;
    if (selectedMatterId === matterId) return;

    try {
      setAddError('');
      await patchMembers({
        matter_id: matterId,
        add: [selectedMatterId],
      }).unwrap();
      setSelectedMatterId('');
    } catch (error) {
      setAddError(
        getErrorMessage(error as FetchBaseQueryError | SerializedError),
      );
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!matterId) return;
    await patchMembers({ matter_id: matterId, remove: [memberId] }).unwrap();
  };

  const handleRemoveFromGroup = async () => {
    await patchMembers({ matter_id: matterId, remove: [matterId] }).unwrap();
    onClose();
  };

  const isAlreadyMember = companions?.members.some(
    (m) => m.id === selectedMatterId,
  );
  const isSelfSelection = !!matterId && selectedMatterId === matterId;
  const isInvalidSelection =
    !selectedMatterId || isAlreadyMember || isSelfSelection;
  let selectionHint = '';
  if (isSelfSelection) selectionHint = 'You cannot add the current case.';
  else if (isAlreadyMember)
    selectionHint = 'This case is already in the group.';

  const hasGroup = Boolean(companions && companions.members.length > 1);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <button
        type="button"
        aria-label="Close manage related cases"
        className="absolute inset-0 bg-black/30"
        onClick={onClose}
      />
      <div
        className="relative mx-4 w-full max-w-lg rounded-lg bg-white p-6 shadow-xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="manage-related-cases-title"
        tabIndex={-1}
      >
        <div className="mb-6 flex items-center justify-between">
          <h2
            id="manage-related-cases-title"
            className="text-lg font-semibold text-zinc-900"
          >
            Manage related cases
          </h2>
          <button
            type="button"
            className="text-sm text-zinc-500 hover:text-zinc-700"
            onClick={onClose}
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div className="space-y-6">
          {!isCurrentPrimary && hasGroup && (
            <div className="text-sm text-zinc-500 bg-zinc-50 p-3 rounded-md">
              To add or remove members, manage related cases from the primary
              case.
            </div>
          )}
          {(!companions || companions.members.length <= 1) && (
            <div className="text-sm text-zinc-600">
              No related cases yet. Link your first related case below.
            </div>
          )}
          <div>
            <div className="text-sm font-medium text-zinc-900 mb-3">
              Primary
            </div>
            <div className="flex items-center gap-3">
              {primaryMember ? (
                <>
                  {primaryMember.id === matterId ? (
                    <span className="text-sm text-zinc-900 font-medium">
                      {primaryMember.display_name}
                    </span>
                  ) : (
                    <Link
                      to={`/matters/${primaryMember.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium"
                    >
                      {primaryMember.display_name}
                    </Link>
                  )}
                  {companions &&
                    companions.members.length === 1 &&
                    isCurrentPrimary && (
                      <button
                        type="button"
                        className="ml-auto text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        onClick={handleRemoveFromGroup}
                        disabled={isPatchingMembers || isMakingPrimary}
                        aria-label="Remove as primary"
                      >
                        {isPatchingMembers
                          ? 'Removing...'
                          : 'Remove as primary'}
                      </button>
                    )}
                </>
              ) : (
                <>
                  <span className="text-sm text-zinc-500">Not set</span>
                  <button
                    type="button"
                    className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={() => matterId && handleMakePrimary(matterId)}
                    disabled={!matterId || isMakingPrimary || isPatchingMembers}
                    aria-label="Set current case as primary"
                  >
                    {isMakingPrimary ? 'Setting...' : 'Make this case primary'}
                  </button>
                </>
              )}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-zinc-900 mb-3">Cases</div>
            <div className="space-y-2">
              {nonPrimaryMembers.length === 0 && (
                <div className="text-sm text-zinc-500">
                  No related cases yet
                </div>
              )}
              {nonPrimaryMembers.map((m) => (
                <div key={m.id} className="flex items-center justify-between">
                  <Link
                    to={`/matters/${m.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:text-blue-700 hover:underline font-medium truncate"
                  >
                    {m.display_name}
                  </Link>
                  {isCurrentPrimary ? (
                    <button
                      type="button"
                      className="text-sm text-red-600 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={() => handleRemoveMember(m.id)}
                      disabled={isPatchingMembers || isMakingPrimary}
                      aria-label={`Remove ${m.display_name}`}
                    >
                      {isPatchingMembers ? 'Removing...' : 'Remove'}
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          </div>

          {isCurrentPrimary ? (
            <div>
              <div className="text-sm font-medium text-zinc-900 mb-3">
                Add member by search
              </div>
              <div className="flex items-center gap-3">
                <div className="w-full">
                  <MatterSelectorCombobox
                    value={selectedMatterId}
                    onChange={setSelectedMatterId}
                    includeAllOption={false}
                    aria-label="Select case to add"
                    placeholder="Search cases by name or ID"
                    firmId={firmId}
                    excludeFilter={(m) =>
                      // Hide if in another companion group
                      Boolean(m.companion_root_id) ||
                      // Hide self
                      m.id === matterId
                    }
                    isOptionDisabled={(m) =>
                      // Disable (but show) if already in THIS group
                      companions?.members.some((cm) => cm.id === m.id) ?? false
                    }
                  />
                  {selectionHint && (
                    <div
                      className="mt-2 text-sm text-zinc-500"
                      aria-live="polite"
                      role="status"
                    >
                      {selectionHint}
                    </div>
                  )}
                  {addError && (
                    <div className="mt-2 text-sm text-red-600" role="alert">
                      {addError}
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleAddSelectedMember}
                  disabled={
                    isPatchingMembers || isMakingPrimary || isInvalidSelection
                  }
                  aria-label="Add selected case to related cases"
                >
                  {isPatchingMembers ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
